import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  Package2,
  ArrowLeft,
  Save,
  Edit,
  History,
  Calendar,
  DollarSign,
  ChevronRight,
  Loader2,
  ShoppingCart,
  Clock,
  Eye,
  Tag,

  FileText,
  Boxes
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatDate, formatPrice } from '../../utils/format';
import { fetchWithAuth } from '../../hooks/useAuth';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  stock: number;
  createdAt: string;
  updatedAt: string;
  orderItems?: Array<{
    id: number;
    quantity: number;
    price: number;
    order: {
      id: number;
      createdAt: string;
      status: string;
    };
  }>;
}

export function ProductDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    price: '',
    stock: '',
  });

  // Fetch product details
  const { data: product, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await fetchWithAuth(`/api/admin/products/${id}`);
      if (!response.ok) throw new Error('Eroare la încărcarea produsului');
      const data = await response.json();
      setFormData({
        name: data.name,
        description: data.description,
        price: String(data.price),
        stock: String(data.stock),
      });
      return data as Product;
    },
  });

  // Update product mutation
  const updateProduct = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await fetchWithAuth(`/api/admin/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name.trim(),
          description: data.description.trim(),
          price: Number(data.price),
          stock: Number(data.stock)
        }),
      });
      if (!response.ok) throw new Error('Eroare la actualizarea produsului');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      toast.success('Produs actualizat cu succes');
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-gray-50">
        <h1 className="text-2xl font-bold mb-4 text-gray-900">Produsul nu a fost găsit</h1>
        <Button 
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => navigate('/products/')}
        >
          <ArrowLeft className="w-4 h-4" />
          Înapoi la produse
        </Button>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProduct.mutate(formData);
  };

  // Calculate sales statistics
  const totalSales = product.orderItems?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const totalRevenue = product.orderItems?.reduce((sum, item) => sum + (item.quantity * Number(item.price)), 0) || 0;
  const averagePrice = totalSales > 0 ? Math.round((totalRevenue / totalSales) * 100) / 100 : 0;

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
            <p className="text-sm text-gray-500">ID: #{product.id}</p>
          </div>
        </div>
        <Button 
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => navigate('/products/')}
        >
          <ArrowLeft className="w-4 h-4" />
          Înapoi la produse
        </Button>
      </div>

      {/* Product Details */}
      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package2 className="w-5 h-5" />
            Detalii Produs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700">Nume</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                    placeholder="Nume produs"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Descriere</label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                    placeholder="Descriere produs"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Preț</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData(f => ({ ...f, price: e.target.value }))}
                    placeholder="Preț produs"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Stoc</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData(f => ({ ...f, stock: e.target.value }))}
                    placeholder="Stoc disponibil"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  className="flex items-center gap-1"
                >
                  Anulează
                </Button>
                <Button 
                  type="submit" 
                  disabled={updateProduct.isPending}
                  className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
                >
                  {updateProduct.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Se salvează...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Salvează
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Tag className="w-4 h-4" />
                  Nume
                </div>
                <div className="text-gray-900">{product.name}</div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <FileText className="w-4 h-4" />
                  Descriere
                </div>
                <div className="text-gray-900">{product.description}</div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <DollarSign className="w-4 h-4" />
                  Preț
                </div>
                <div className="text-gray-900">{formatPrice(product.price)}</div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Boxes className="w-4 h-4" />
                  Stoc
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-900">{product.stock}</span>
                  <Badge className={
                    product.stock > 10
                      ? 'bg-green-100 text-green-800 ring-1 ring-green-200'
                      : product.stock > 0
                      ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200'
                      : 'bg-red-100 text-red-800 ring-1 ring-red-200'
                  }>
                    {product.stock > 10
                      ? 'În Stoc'
                      : product.stock > 0
                      ? 'Stoc Limitat'
                      : 'Stoc Epuizat'}
                  </Badge>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4" />
                  Data Adăugării
                </div>
                <div className="text-gray-900">{formatDate(product.createdAt)}</div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4" />
                  Ultima Actualizare
                </div>
                <div className="text-gray-900">{formatDate(product.updatedAt)}</div>
              </div>
              <div className="col-span-2">
                <Button 
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1"
                >
                  <Edit className="w-4 h-4" />
                  Editează
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sales Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <ShoppingCart className="w-4 h-4" />
              Total Vânzări
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {totalSales} bucăți
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <DollarSign className="w-4 h-4" />
              Venit Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(totalRevenue)}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <DollarSign className="w-4 h-4" />
              Preț Mediu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatPrice(averagePrice)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order History */}
      <Card className="bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Istoric Comenzi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Comandă</TableHead>
                <TableHead className="font-semibold">Data</TableHead>
                <TableHead className="font-semibold">Cantitate</TableHead>
                <TableHead className="font-semibold">Preț</TableHead>
                <TableHead className="font-semibold">Total</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!product.orderItems?.length ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="text-gray-500">Nu există comenzi</div>
                  </TableCell>
                </TableRow>
              ) : (
                product.orderItems.map((item) => (
                  <TableRow key={item.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">#{item.order.id}</TableCell>
                    <TableCell>{formatDate(item.order.createdAt)}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{formatPrice(item.price)}</TableCell>
                    <TableCell>{formatPrice(item.quantity * Number(item.price))}</TableCell>
                    <TableCell>
                      <Badge className={
                        item.order.status === 'DELIVERED'
                          ? 'bg-green-100 text-green-800 ring-1 ring-green-200'
                          : item.order.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200'
                          : 'bg-gray-100 text-gray-800 ring-1 ring-gray-200'
                      }>
                        {item.order.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1"
                        onClick={() => navigate(`/orders/${item.order.id}/`)}
                      >
                        <Eye className="w-4 h-4" />
                        Detalii
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
